import useSharedState from 'src/hooks/globalState/useSharedState';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import useEffectUntilDone from 'src/hooks/useEffectUtils/useEffectUntilDone';
import { v4 as uuidv4 } from 'uuid';
export var NoteEditorChangeType;
(function (NoteEditorChangeType) {
    NoteEditorChangeType["NOTE_DATA_CHANGED"] = "NOTE_DATA_CHANGED";
    NoteEditorChangeType["NOTE_TAB_SEQUENCE_CHANGED"] = "NOTE_TAB_SEQUENCE_CHANGED";
    NoteEditorChangeType["NOTE_TAB_CHANGED"] = "NOTE_TAB_CHANGED";
})(NoteEditorChangeType || (NoteEditorChangeType = {}));
export function isNoteTabChange(p) {
    return p.changeType === NoteEditorChangeType.NOTE_TAB_CHANGED;
}
export function isNoteDataChange(p) {
    return p.changeType === NoteEditorChangeType.NOTE_DATA_CHANGED;
}
export function isNoteTabSequenceChange(p) {
    return p.changeType === NoteEditorChangeType.NOTE_TAB_SEQUENCE_CHANGED;
}
function getInitialNoteState(note) {
    if (!note)
        return {
            id: '',
            data: {
                title: '',
                topics: [],
            },
        };
    return {
        id: note.id,
        data: {
            title: note.data.title,
            topics: [...note.data.topics],
        },
    };
}
function getInitialNoteTabsState(note) {
    if (!note)
        return new Map();
    return new Map(note.tabs.map((tab) => [tab.id, {
            id: tab.id,
            data: {
                title: tab.data.title ?? '',
                content: tab.data.content ?? '',
                version: tab.data.version ?? 0,
            },
        }]));
}
function hasNoteTabChangedFromBaseTab(tab, baseTab) {
    if (tab.data.clientKey)
        return true;
    return tab.data.title !== baseTab.data.title ||
        tab.data.content !== baseTab.data.content;
}
function hasTabSequenceChangedFromBaseNote(baseNote, sequence, tabs) {
    const beforeIds = baseNote.tabs.map((t) => t.id).join('\0');
    const afterIds = sequence
        .filter((id) => tabs.has(id))
        .join('\0');
    return beforeIds !== afterIds;
}
export default function useNoteEditorDraft(baseNote, options) {
    const storageKeySuffix = options?.storageKeySuffix ?? '';
    const storageKey = (prefix) => `${prefix}${baseNote?.id ?? ''}${storageKeySuffix}`;
    const [note, setNote] = useSharedState(storageKey('note-editor-draft-'), getInitialNoteState(baseNote));
    const [noteTabs, setNoteTabs] = useSharedState(storageKey('note-editor-draft-tabs-'), getInitialNoteTabsState(baseNote));
    const [noteTabsSequence, setNoteTabsSequence] = useSharedState(storageKey('note-editor-draft-tabs-sequence-'), []);
    const updateNote = useStableCallback((data) => {
        setNote((prev) => ({
            ...prev,
            data: {
                ...prev.data,
                title: data.title.trim(),
                topics: data.topics,
            },
        }));
    });
    const updateNoteTabTitle = useStableCallback((tabID, title) => {
        setNoteTabs((prev) => {
            const next = new Map(prev);
            const tab = next.get(tabID);
            if (tab) {
                tab.data.title = title.trim();
            }
            return next;
        });
    });
    const updateNoteTabContent = useStableCallback((tabID, content) => {
        setNoteTabs((prev) => {
            const next = new Map(prev);
            const tab = next.get(tabID);
            if (tab) {
                tab.data.content = content;
            }
            return next;
        });
    });
    const appendNoteTab = useStableCallback((title) => {
        const clientKey = uuidv4();
        setNoteTabs((prev) => {
            const next = new Map(prev);
            next.set(clientKey, {
                id: clientKey,
                data: { title: title.trim(), content: '', version: 0, clientKey },
            });
            return next;
        });
        setNoteTabsSequence((prev) => [...prev, clientKey]);
        return clientKey;
    });
    const removeNoteTab = useStableCallback((tabID) => {
        setNoteTabs((prev) => {
            const next = new Map(prev);
            next.delete(tabID);
            return next;
        });
        setNoteTabsSequence((prev) => prev.filter((id) => id !== tabID));
    });
    const rearrangeNoteTabs = useStableCallback((newSequence) => {
        setNoteTabsSequence(newSequence);
    });
    const reset = useStableCallback(() => {
        if (!baseNote)
            return;
        setNote(getInitialNoteState(baseNote));
        setNoteTabs(getInitialNoteTabsState(baseNote));
        setNoteTabsSequence(baseNote.tabs.map((tab) => tab.id));
    });
    const applyUpdates = useStableCallback((updates) => {
        setNote(prevState => ({
            ...prevState,
            data: {
                ...prevState.data,
                title: updates.data?.title ?? prevState.data.title,
                topics: updates.data?.topics ?? prevState.data.topics,
            },
        }));
        setNoteTabs(prevState => {
            const next = new Map();
            for (const tab of updates.tabs ?? []) {
                const tabID = tab.id ?? tab.data.clientKey;
                const baseTab = prevState.get(tabID);
                next.set(tabID, {
                    id: tabID,
                    data: {
                        ...baseTab?.data,
                        ...tab.data,
                        title: tab.data.title ?? baseTab?.data.title ?? '',
                        content: tab.data.content ?? baseTab?.data.content ?? '',
                        version: tab.data.version ?? 0,
                        clientKey: tab.data.clientKey,
                    },
                });
            }
            return next;
        });
        setNoteTabsSequence((updates.tabs ?? []).map((t) => t.id ?? t.data.clientKey));
    });
    const getChangesPayload = useStableCallback(() => {
        if (!baseNote)
            return { data: { id: '' } };
        const payload = { id: note.id };
        const changes = getChanges();
        const noteDataChanges = changes.find(isNoteDataChange);
        if (noteDataChanges) {
            const dataPatch = {};
            if (noteDataChanges.changes.title.hasChanged) {
                dataPatch.title = noteDataChanges.changes.title.newData ?? '';
            }
            if (noteDataChanges.changes.topics.hasChanged) {
                dataPatch.topics = noteDataChanges.changes.topics.newData ?? [];
            }
            if (dataPatch.title !== undefined || dataPatch.topics !== undefined) {
                payload.data = dataPatch;
            }
        }
        const tabsDirty = tabSequenceIdsDirty(baseNote, noteTabsSequence, noteTabs) ||
            noteTabsSequence.some((id) => hasNoteTabChanged(id));
        if (tabsDirty) {
            const changesByTabId = tabChangeByTabId(changes);
            payload.tabs = noteTabsSequence.map((tabID) => {
                const row = noteTabs.get(tabID);
                if (!row) {
                    throw new Error(`NoteEditor: missing draft tab "${tabID}"`);
                }
                if (row.data.clientKey) {
                    return {
                        data: {
                            clientKey: row.data.clientKey,
                            title: row.data.title,
                            content: row.data.content,
                        },
                    };
                }
                const tabChanges = changesByTabId.get(row.id);
                const tabData = {};
                if (tabChanges) {
                    if (tabChanges.changes.title.hasChanged && tabChanges.changes.title.newData != null) {
                        tabData.title = tabChanges.changes.title.newData;
                    }
                    if (tabChanges.changes.content.hasChanged && tabChanges.changes.content.newData != null) {
                        tabData.content = tabChanges.changes.content.newData;
                    }
                    if (tabData.title !== undefined || tabData.content !== undefined) {
                        tabData.version = tabChanges.version;
                    }
                }
                return {
                    id: row.id,
                    data: tabData,
                };
            });
        }
        return { data: payload };
    });
    const hasNoteTabChanged = (tabID) => {
        const tab = noteTabs.get(tabID);
        if (!tab || !baseNote)
            return false;
        const baseTab = baseNote.tabs.find((t) => t.id === tabID);
        if (!baseTab)
            return true;
        return hasNoteTabChangedFromBaseTab(tab, baseTab);
    };
    const hasUnsavedChanges = () => {
        if (!baseNote)
            return false;
        if (note.data.title !== baseNote.data.title ||
            JSON.stringify(note.data.topics) !== JSON.stringify(baseNote.data.topics)) {
            return true;
        }
        if (hasTabSequenceChangedFromBaseNote(baseNote, noteTabsSequence, noteTabs)) {
            return true;
        }
        return noteTabsSequence.some(hasNoteTabChanged);
    };
    const getChanges = useStableCallback(() => {
        if (!baseNote)
            return [];
        const out = [];
        const noteTitleChanged = note.data.title !== baseNote.data.title;
        const noteTopicsChanged = JSON.stringify(note.data.topics) !== JSON.stringify(baseNote.data.topics);
        if (noteTitleChanged || noteTopicsChanged) {
            out.push({
                changeType: NoteEditorChangeType.NOTE_DATA_CHANGED,
                changes: {
                    title: noteTitleChanged
                        ? { hasChanged: true, oldData: baseNote.data.title, newData: note.data.title }
                        : { hasChanged: false, data: note.data.title },
                    topics: noteTopicsChanged
                        ? {
                            hasChanged: true,
                            oldData: [...baseNote.data.topics],
                            newData: [...note.data.topics],
                        }
                        : { hasChanged: false, data: [...note.data.topics] },
                },
            });
        }
        const baselineById = new Map((baseNote.tabs ?? []).map((t) => [t.id, t]));
        const draftIdSet = new Set(noteTabsSequence);
        for (const baseTab of baseNote.tabs ?? []) {
            if (!draftIdSet.has(baseTab.id)) {
                const oldT = baseTab.data.title ?? '';
                const oldC = baseTab.data.content ?? '';
                out.push({
                    tabID: baseTab.id,
                    version: baseTab.data.version ?? 0,
                    changeType: NoteEditorChangeType.NOTE_TAB_CHANGED,
                    changes: {
                        title: { hasChanged: true, oldData: oldT, newData: null },
                        content: { hasChanged: true, oldData: oldC, newData: null },
                    },
                });
            }
        }
        for (const id of noteTabsSequence) {
            const row = noteTabs.get(id);
            if (!row)
                continue;
            if (row.data.clientKey) {
                const newT = row.data.title ?? '';
                const newC = row.data.content ?? '';
                out.push({
                    tabID: row.id,
                    version: 0,
                    changeType: NoteEditorChangeType.NOTE_TAB_CHANGED,
                    changes: {
                        title: { hasChanged: true, oldData: null, newData: newT },
                        content: { hasChanged: true, oldData: null, newData: newC },
                    },
                });
                continue;
            }
            const b = baselineById.get(row.id);
            if (!b)
                continue;
            const oldC = b.data.content ?? '';
            const newC = row.data.content ?? '';
            const oldT = b.data.title ?? '';
            const newT = row.data.title ?? '';
            const titleChanged = oldT !== newT;
            const contentChanged = oldC !== newC;
            if (!titleChanged && !contentChanged)
                continue;
            out.push({
                tabID: row.id,
                version: row.data.version ?? 0,
                changeType: NoteEditorChangeType.NOTE_TAB_CHANGED,
                changes: {
                    title: titleChanged
                        ? { hasChanged: true, oldData: oldT, newData: newT }
                        : { hasChanged: false, data: newT },
                    content: contentChanged
                        ? { hasChanged: true, oldData: oldC, newData: newC }
                        : { hasChanged: false, data: newC },
                },
            });
        }
        const beforeSequenceRows = baseNote.tabs.map((t) => ({
            id: t.id,
            title: t.data.title ?? '',
        }));
        const afterSequenceRows = noteTabsSequence
            .map((id) => {
            const row = noteTabs.get(id);
            if (!row)
                return null;
            return { id: row.id, title: row.data.title ?? '' };
        })
            .filter((r) => r != null);
        if (hasTabSequenceChangedFromBaseNote(baseNote, noteTabsSequence, noteTabs)) {
            out.push({
                changeType: NoteEditorChangeType.NOTE_TAB_SEQUENCE_CHANGED,
                changes: {
                    before: beforeSequenceRows,
                    after: afterSequenceRows,
                },
            });
        }
        return out;
    });
    const getNoteTabs = useStableCallback(() => {
        return noteTabsSequence.map((id) => noteTabs.get(id));
    });
    const getNoteTabContent = useStableCallback((tabID) => {
        const tab = noteTabs.get(tabID);
        if (!tab)
            return '';
        return tab.data.content;
    });
    const getPreviousNoteTab = useStableCallback((index) => {
        if (index === 0)
            return null;
        return noteTabs.get(noteTabsSequence[index - 1]);
    });
    const getNextNoteTab = useStableCallback((index) => {
        if (index === noteTabsSequence.length - 1)
            return null;
        return noteTabs.get(noteTabsSequence[index + 1]);
    });
    useEffectUntilDone((done) => {
        if (!baseNote)
            return;
        reset();
        done();
    }, [baseNote]);
    const state = {
        note,
        noteTabs,
        noteTabsSequence,
    };
    const actions = {
        updateNote,
        updateNoteTabTitle,
        updateNoteTabContent,
        appendNoteTab,
        removeNoteTab,
        rearrangeNoteTabs,
        reset,
        applyUpdates,
    };
    const helpers = {
        getNoteTabs,
        hasNoteTabChanged,
        hasUnsavedChanges,
        getChanges,
        getChangesPayload,
        getNoteTabContent,
        getPreviousNoteTab,
        getNextNoteTab,
    };
    return [state, actions, helpers];
}
function tabSequenceIdsDirty(baseNote, sequence, tabs) {
    const beforeIds = (baseNote.tabs ?? []).map((t) => t.id).join('\0');
    const afterIds = sequence.filter((id) => tabs.has(id)).join('\0');
    return beforeIds !== afterIds;
}
function tabChangeByTabId(changes) {
    const map = new Map();
    for (const change of changes) {
        if (isNoteTabChange(change)) {
            map.set(change.tabID, change);
        }
    }
    return map;
}
